import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Dayjs } from 'dayjs';

interface LicensesReturnFilterState {
  searchText: string;
  selectedLicenseTypeIds: number[];
  selectedAppIds: boolean[];
  selectedReturnStatusIds: string[];
  selectedOrganizationalEntityIds: number[];
  fromFilter: Dayjs | null | undefined;
  toFilter: Dayjs | null | undefined;
  selectedLicenseTypeIdsTemporal: number[];
  selectedAppIdsTemporal: boolean[];
  selectedReturnStatusIdsTemporal: string[];
  selectedOrganizationalEntityIdsTemporal: number[];
}

const initialState: LicensesReturnFilterState = {
  searchText: '',
  selectedLicenseTypeIds: [],
  selectedAppIds: [],
  selectedReturnStatusIds: [],
  selectedOrganizationalEntityIds: [],
  fromFilter: undefined,
  toFilter: undefined,
  selectedLicenseTypeIdsTemporal: [],
  selectedAppIdsTemporal: [],
  selectedReturnStatusIdsTemporal: [],
  selectedOrganizationalEntityIdsTemporal: [],
};

const licensesReturnFiltersSlice = createSlice({
  name: 'licensesReturnFilters',
  initialState,
  reducers: {
    setSearchText(state, action: PayloadAction<string>) {
      state.searchText = action.payload;
    },
    setSelectedLicenseTypeIds(state, action: PayloadAction<number[]>) {
      state.selectedLicenseTypeIds = action.payload;
    },
    setSelectedAppIds(state, action: PayloadAction<boolean[]>) {
      state.selectedAppIds = action.payload;
    },
    setSelectedReturnStatusIds(state, action: PayloadAction<string[]>) {
      state.selectedReturnStatusIds = action.payload;
    },
    setSelectedOrganizationalEntityIds(state, action: PayloadAction<number[]>) {
      state.selectedOrganizationalEntityIds = action.payload;
    },
    setFromFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.fromFilter = action.payload;
    },
    setToFilter(state, action: PayloadAction<Dayjs | null | undefined>) {
      state.toFilter = action.payload;
    },
    setSelectedLicenseTypeIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedLicenseTypeIdsTemporal = action.payload;
    },
    setSelectedAppIdsTemporal(state, action: PayloadAction<boolean[]>) {
      state.selectedAppIdsTemporal = action.payload;
    },
    setSelectedReturnStatusIdsTemporal(state, action: PayloadAction<string[]>) {
      state.selectedReturnStatusIdsTemporal = action.payload;
    },
    setSelectedOrganizationalEntityIdsTemporal(state, action: PayloadAction<number[]>) {
      state.selectedOrganizationalEntityIdsTemporal = action.payload;
    },
    resetFilters(state) {
      state.searchText = initialState.searchText;
      state.selectedLicenseTypeIds = initialState.selectedLicenseTypeIds;
      state.selectedAppIds = initialState.selectedAppIds;
      state.selectedReturnStatusIds = initialState.selectedReturnStatusIds;
      state.selectedOrganizationalEntityIds = initialState.selectedOrganizationalEntityIds;
      state.fromFilter = initialState.fromFilter;
      state.toFilter = initialState.toFilter;
      state.selectedLicenseTypeIdsTemporal = initialState.selectedLicenseTypeIdsTemporal;
      state.selectedAppIdsTemporal = initialState.selectedAppIdsTemporal;
      state.selectedReturnStatusIdsTemporal = initialState.selectedReturnStatusIdsTemporal;
      state.selectedOrganizationalEntityIdsTemporal = initialState.selectedOrganizationalEntityIdsTemporal;
    },
  },
});

export const {
  setSearchText,
  setSelectedLicenseTypeIds,
  setSelectedAppIds,
  setSelectedReturnStatusIds,
  setSelectedOrganizationalEntityIds,
  setFromFilter,
  setToFilter,
  setSelectedLicenseTypeIdsTemporal,
  setSelectedAppIdsTemporal,
  setSelectedReturnStatusIdsTemporal,
  setSelectedOrganizationalEntityIdsTemporal,
  resetFilters,
} = licensesReturnFiltersSlice.actions;

export const areFiltersAppliedSelector = (state: { licensesReturnFilters: LicensesReturnFilterState }) => {
  const {
    searchText,
    selectedLicenseTypeIds,
    selectedAppIds,
    selectedReturnStatusIds,
    selectedOrganizationalEntityIds,
    fromFilter,
    toFilter,
  } = state.licensesReturnFilters;

  return (
    searchText.length > 0 ||
    selectedLicenseTypeIds.length > 0 ||
    selectedAppIds.length > 0 ||
    selectedReturnStatusIds.length > 0 ||
    selectedOrganizationalEntityIds.length > 0 ||
    fromFilter ||
    toFilter
  );
};

export const activeFiltersCountSelector = (state: { licensesReturnFilters: LicensesReturnFilterState }) => {
  const {
    searchText,
    selectedLicenseTypeIds,
    selectedAppIds,
    selectedReturnStatusIds,
    selectedOrganizationalEntityIds,
    fromFilter,
    toFilter,
  } = state.licensesReturnFilters;

  let count = 0;
  if (searchText.length > 0) count++;
  if (selectedLicenseTypeIds.length > 0) count++;
  if (selectedAppIds.length > 0) count++;
  if (selectedReturnStatusIds.length > 0) count++;
  if (selectedOrganizationalEntityIds.length > 0) count++;
  if (fromFilter) count++;
  if (toFilter) count++;

  return count;
};

export const licensesReturnFiltersActions = licensesReturnFiltersSlice.actions;

export const licensesReturnFilters = licensesReturnFiltersSlice.reducer;
